import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Send, Loader2, CalendarCheck, Bot, User } from "lucide-react";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

export default function ReceptionistChat({ channel = "public", greeting, initialOptions = [] }) {
  const [sessionId] = useState(uid);
  const [messages, setMessages] = useState([
    { id: uid(), role: "assistant", text: greeting || "Hi! I'm the booking assistant. What would you like to book in today?" },
  ]);
  const [slots, setSlots] = useState([]);
  const [options, setOptions] = useState(initialOptions);
  const [booking, setBooking] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setSlots([]);
    setOptions([]);
    setMessages((m) => [...m, { id: uid(), role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/receptionist/chat", { session_id: sessionId, message: msg, channel });
      setMessages((m) => [...m, { id: uid(), role: "assistant", text: data.reply }]);
      setSlots(data.slots || []);
      setOptions(data.options || []);
      if (data.done && data.booking?.ok) setBooking(data.booking);
    } catch {
      setMessages((m) => [...m, { id: uid(), role: "assistant", text: "Sorry, something went wrong. Please try again or call the clinic." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="receptionist-chat">
      <div className="flex-1 space-y-4 overflow-auto p-5">
        {messages.map((m) => (
          <div key={m.id} className={`flex items-start gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.role}`}>
            <div className={`grid h-8 w-8 flex-shrink-0 place-items-center rounded-full ${m.role === "user" ? "bg-slate-200 text-slate-600" : "bg-teal-600 text-white"}`}>
              {m.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
            </div>
            <div className={`max-w-[85%] sm:max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-800"}`}>
              {m.text}
            </div>
          </div>
        ))}

        {options.length > 0 && !loading && (
          <div className="flex flex-wrap gap-2 pl-10" data-testid="chat-options">
            {options.map((o) => (
              <button key={o.id} onClick={() => send(o.name)} data-testid={`chat-option-${o.id}`}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-[13px] font-semibold text-slate-700 transition-colors hover:border-teal-400 hover:bg-teal-50 active:bg-teal-100">
                {o.name}
              </button>
            ))}
          </div>
        )}

        {slots.length > 0 && !loading && (
          <div className="flex flex-wrap gap-2 pl-10" data-testid="chat-slots">
            {slots.map((s) => (
              <button key={s.start} onClick={() => send(s.label)} data-testid={`chat-slot-${s.start}`}
                className="rounded-full border border-teal-300 bg-teal-50 px-4 py-2 text-[13px] font-semibold text-teal-700 transition-colors hover:bg-teal-100 active:bg-teal-200">
                {s.label}
              </button>
            ))}
          </div>
        )}

        {booking && (
          <div className="ml-10 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="chat-booking-confirmed">
            <CalendarCheck className="h-5 w-5 flex-shrink-0" />
            {booking.action === "cancel" ? (
              <span><b>Cancelled:</b> your appointment on {booking.when}.</span>
            ) : booking.action === "reschedule" ? (
              <span><b>Moved:</b> your appointment is now {booking.when}.</span>
            ) : (
              <span><b>Booked:</b> {booking.service} with {booking.practitioner} — {booking.when} at {booking.location}.</span>
            )}
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 pl-10 text-sm text-slate-400" data-testid="chat-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> typing…
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-slate-200 p-3">
        <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="chat-input" placeholder="Type your message…"
          autoComplete="off" enterKeyHint="send" aria-label="Type your message"
          className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-base focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-teal-500" />
        <button type="submit" disabled={loading || !input.trim()} data-testid="chat-send"
          className="grid h-10 w-10 place-items-center rounded-xl bg-teal-600 text-white transition-colors hover:bg-teal-700 disabled:opacity-50">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
